import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import MaskedLines from "@/components/MaskedLines";
import { ArrowDown, ArrowRight } from "@phosphor-icons/react";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 180]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.3]);

  const [time, setTime] = useState("");
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const rome = new Intl.DateTimeFormat("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Rome",
      }).format(now);
      setTime(rome);
    };
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      data-testid="section-hero"
      className="relative min-h-[100svh] w-full flex flex-col overflow-hidden bg-navy-dark"
    >
      {/* Layered background: subtle grid + spotlight + parallax texture */}
      <motion.div
        aria-hidden
        style={{ y }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute inset-0 spotlight opacity-80" />
        {/* thin gold grid lines */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#c9a84c" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        {/* soft radial gold blob top-right */}
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-gold/10 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-navy-light/60 blur-[100px]" />
      </motion.div>

      {/* Top meta bar */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 pt-28 md:pt-32 px-6 md:px-10 lg:px-14 max-w-[1440px] mx-auto w-full"
      >
        <div className="flex items-center justify-between text-[0.7rem] uppercase text-ink/50" style={{ letterSpacing: "0.25em" }}>
          <span className="font-sans-crisp flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-gold animate-gold-pulse" />
            <span data-testid="hero-established">Nord Est Italia</span>
          </span>
          <span className="font-sans-crisp hidden md:inline">
            Treviso · {time}
          </span>
          <span className="font-sans-crisp">MMXXIV</span>
        </div>
      </motion.div>

      {/* Main headline */}
      <div className="relative z-10 flex-1 flex items-center px-6 md:px-10 lg:px-14 max-w-[1440px] mx-auto w-full">
        <div className="w-full grid grid-cols-12 gap-6 pt-16 md:pt-20 pb-24">
          <div className="col-span-12 lg:col-span-11">
            {/* eyebrow */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center gap-4 mb-10 md:mb-14"
            >
              <span className="w-14 h-px bg-gold" />
              <span
                className="text-[0.7rem] md:text-xs uppercase text-gold font-sans-crisp font-semibold"
                style={{ letterSpacing: "0.3em" }}
                data-testid="hero-eyebrow"
              >
                Consulenza Strategica per PMI
              </span>
            </motion.div>

            <MaskedLines
              testId="hero-headline"
              as="h1"
              startDelay={0.9}
              className="font-display font-medium tracking-tighter leading-[0.92] text-[3rem] sm:text-[4rem] md:text-[5.5rem] lg:text-[7rem] xl:text-[8rem] text-ink"
              lines={[
                'La tua azienda ha il',
                '<em class="italic-serif text-gold font-normal">potenziale</em>.',
                'Noi ti aiutiamo a',
                '<em class="italic-serif text-gold font-normal">liberarlo</em>.',
              ]}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 2.0, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-subheadline"
              className="mt-12 md:mt-16 max-w-[540px] text-lg md:text-xl text-ink/70 leading-relaxed font-sans-crisp"
            >
              Consulenza strategica per imprenditori di PMI nel Nord Est Italia.
              Oltre vent'anni di esperienza da Amministratore Delegato al servizio della tua crescita.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 2.2, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 md:mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-6"
            >
              <a
                href="#diagnosi"
                data-testid="hero-cta-primary"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#diagnosi");
                  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40, duration: 1.6 });
                  else el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group relative inline-flex items-center gap-4 bg-gold text-navy-dark px-8 py-5 uppercase font-sans-crisp font-semibold text-[0.78rem] transition-transform duration-500 hover:scale-[1.02] overflow-hidden"
                style={{ letterSpacing: "0.22em" }}
              >
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                <span className="relative">Scopri il Pacchetto Diagnosi</span>
                <ArrowRight size={16} weight="bold" className="relative transition-transform duration-500 group-hover:translate-x-1" />
              </a>

              <a
                href="#chi-siamo"
                data-testid="hero-cta-secondary"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.querySelector("#chi-siamo");
                  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -40, duration: 1.6 });
                  else el?.scrollIntoView({ behavior: "smooth" });
                }}
                className="group inline-flex items-center gap-4 border border-gold text-gold bg-transparent hover:bg-gold/10 hover:border-gold-light hover:text-gold-light px-8 py-5 uppercase font-sans-crisp font-semibold text-[0.78rem] transition-colors duration-500"
                style={{ letterSpacing: "0.22em" }}
              >
                <span>Chi è Marco Danieli</span>
                <ArrowRight size={16} weight="bold" className="transition-transform duration-500 group-hover:translate-x-1" />
              </a>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom rail: scroll indicator + stats teaser */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 border-t border-gold/15 bg-navy-dark/40 backdrop-blur-sm"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 py-5 flex flex-wrap items-center justify-between gap-y-4 text-[0.72rem] uppercase text-ink/60 font-sans-crisp" style={{ letterSpacing: "0.22em" }}>
          <div className="flex items-center gap-3">
            <ArrowDown size={14} weight="light" className="animate-bounce text-gold" />
            <span data-testid="hero-scroll-hint">Scorri per scoprire</span>
          </div>
          <div className="flex items-center gap-8">
            <span><span className="text-gold font-display text-sm italic mr-2">20+</span> anni CEO</span>
            <span className="hidden md:inline"><span className="text-gold font-display text-sm italic mr-2">6</span> settori</span>
            <span className="hidden md:inline"><span className="text-gold font-display text-sm italic mr-2">1</span> obiettivo: la tua crescita</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
